/**
 * Entry point della shell ERP Floraltech.
 *
 * Monta l'applicazione React con BrowserRouter e ThemeProvider.
 * Il CSS di @floraltech/ui viene importato qui una sola volta
 * e copre tutti i moduli caricati nella shell.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster, TooltipProvider } from '@floraltech/ui';
import App from './shell/App';
import { AuthProvider } from './shell/AuthContext';
import { ConfigProvider } from './shell/ConfigContext';
import { ThemeProvider } from './shell/ThemeContext';
import { UOProvider } from './shell/UOContext';
import './i18n';
import './styles.css';

/**
 * DEC-057: wrap globale di fetch per garantire `credentials: 'include'`
 * a tutte le chiamate, così il cookie httpOnly `jwt` viene sempre inviato
 * con le richieste same-origin verso il backend ERP.
 *
 * Durante la fase transitoria (backend supporta sia cookie che Bearer header)
 * questo non rompe i componenti che passano ancora `Authorization: Bearer ...`.
 */
const _origFetch = window.fetch;
window.fetch = function patchedFetch(input, init) {
  const finalInit: RequestInit = init ? { ...init } : {};
  if (finalInit.credentials === undefined) {
    finalInit.credentials = 'include';
  }
  return _origFetch.call(window, input, finalInit);
};

const root = document.getElementById('root');
if (!root) throw new Error('Elemento #root non trovato nel DOM');

createRoot(root).render(
  <React.StrictMode>
    <TooltipProvider delayDuration={300}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ThemeProvider>
          <AuthProvider>
            <UOProvider>
              <ConfigProvider>
                <App />
                {/* Il Toaster va montato QUI, alla radice: ui_core lo monta
                    dentro AppShell, ma AppShell non e' usato da nessuna parte
                    (la shell e' Layout.tsx). Senza questa riga ogni `toast(...)`
                    del prodotto viene chiamato e non mostra niente: l'azione
                    riesce e l'utente non vede alcun riscontro, che e' il difetto
                    peggiore fra quelli silenziosi. Trovato da dali il 2026-08-01
                    dopo che i primi toast erano gia' andati in produzione. */}
                <Toaster />
              </ConfigProvider>
            </UOProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </TooltipProvider>
  </React.StrictMode>
);
